import { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormHelperText, Typography } from '@mui/material';
import StopRounded from '@mui/icons-material/StopRounded';
import { useStopTimer } from '../api/time-tracking';
import { formatDelayLabel, formatDuration } from '../lib/elapsed';
import { apiErrorMessage } from '../lib/api';
import { isHtmlEmpty, notifyError, notifySuccess } from '../lib/notify';
import { RichTextEditor } from './common';

/**
 * "What did you complete?" prompt shown whenever a running task timer is stopped. The note is
 * required and stored on the time entry, where it feeds the timesheet.
 */
export function EndTaskModal({ taskId, taskTitle, open, onClose }: { taskId: string; taskTitle?: string; open: boolean; onClose: () => void }) {
  const stopTimer = useStopTimer(taskId);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const submit = async () => {
    if (isHtmlEmpty(note)) {
      setError('Describe what you completed before ending the task');
      return;
    }
    try {
      const result = await stopTimer.mutateAsync(note);
      const parts: string[] = [`Logged ${formatDuration(result.totalSeconds)} total`];
      if (result.startDelaySeconds !== null) parts.push(`started ${formatDelayLabel(result.startDelaySeconds)}`);
      if (result.endDelaySeconds !== null) parts.push(`finished ${formatDelayLabel(result.endDelaySeconds)}`);
      setNote('');
      setError('');
      onClose();
      notifySuccess('Task timer stopped', parts.join(' · '));
    } catch (err) {
      notifyError(apiErrorMessage(err, 'Could not stop timer'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>What did you complete?</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {taskTitle ? `For "${taskTitle}" — ` : ''}This note is saved with today&apos;s time entry and shows up on your timesheet.
        </Typography>
        <RichTextEditor
          value={note}
          onChange={(html) => {
            setNote(html);
            if (error) setError('');
          }}
          minHeight={90}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" startIcon={<StopRounded />} disabled={stopTimer.isPending} onClick={submit}>
          End task
        </Button>
      </DialogActions>
    </Dialog>
  );
}
